import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Plus } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, PageHeader, Skeleton } from '@mkcore/ui';
import { formatDate } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { ArticleDialog, articlesQuery } from '@/features/support/articles';

const search = z.object({
  q: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === undefined ? undefined : String(v))),
  category: z.string().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/support/articles/')({ validateSearch: search, component: ArticlesPage });

/** The knowledge base light (CO-0495, CO-0496): search in full text, browse per category, write a new article. */
function ArticlesPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const canWrite = useCan('support:update');
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const q = useInfiniteQuery(articlesQuery({ q: s.q || undefined, category: s.category, limit: 50 }));
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const categories = q.data?.pages[0]?.categories ?? [];
  return (
    <>
      <PageHeader
        title={t('articles.title')}
        description={t('articles.description')}
        actions={
          canWrite && (
            <Button onClick={() => setAdding(true)}>
              <Plus /> {t('articles.new')}
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Field label={t('articles.search')} className="w-80">
          {(p) => <Input {...p} value={s.q ?? ''} placeholder={t('articles.searchHint')} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
      </div>
      {!!categories.length && (
        <div className="mb-4 flex flex-wrap gap-1" role="group" aria-label={t('articles.fields.category')}>
          <Button size="sm" variant={!s.category ? 'secondary' : 'ghost'} aria-pressed={!s.category} onClick={() => set({ category: undefined })}>
            {t('common.all')}
          </Button>
          {categories.map((c) => (
            <Button key={c.name} size="sm" variant={s.category === c.name ? 'secondary' : 'ghost'} aria-pressed={s.category === c.name} onClick={() => set({ category: c.name })}>
              {c.name} <span className="text-fg-3">{c.count}</span>
            </Button>
          ))}
        </div>
      )}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !items.length ? (
        <EmptyState title={t('articles.none')} description={canWrite ? t('articles.emptyHint') : undefined} />
      ) : (
        <ul className="grid gap-3" aria-label={t('articles.title')}>
          {items.map((a) => (
            <li key={a.id}>
              <Card className="grid gap-1 p-4">
                <span className="flex flex-wrap items-center gap-2">
                  <Link to="/support/articles/$id" params={{ id: a.id }} className="font-bold hover:underline">
                    {a.title}
                  </Link>
                  {a.category && <Badge tone="outline">{a.category}</Badge>}
                  {a.organisationName && <Badge tone="info">{a.organisationName}</Badge>}
                </span>
                <span className="text-sm text-fg-2">{a.excerpt}</span>
                <span className="text-xs text-fg-3">
                  {formatDate(a.updatedAt.slice(0, 10))}
                  {!!a.tickets.length && ` · ${t('articles.helped', { count: a.tickets.length })}`}
                </span>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      {adding && <ArticleDialog onClose={() => setAdding(false)} />}
    </>
  );
}
